import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Linkedin } from 'lucide-react';

const team: { name: string; role: string; image: string; imagePosition?: string; description: string; color: string; url: string }[] = [
  {
    name: 'Dr. Jayanthi Subramanian',
    role: 'Co-founder & CEO',
    image: '/Jayanthi.jpg',
    description: 'Healthcare visionary with deep expertise in behavioral health integration and CMS compliance. Leads Doctorite\'s mission to transform mental healthcare through AI innovation.',
    color: 'from-brand-500 to-brand-600',
    url: 'https://www.linkedin.com/in/jaysub'
  },
  {
    name: 'Madhusudhan Reddy Raghunayakula',
    role: 'Co-founder & COO',
    image: '/Madhusudhan.png',
    imagePosition: 'object-top',
    description: 'Operations leader driving Doctorite\'s growth and clinical service delivery. Ensures seamless implementation for healthcare organizations nationwide.',
    color: 'from-accent-500 to-emerald-600',
    url: 'https://www.linkedin.com/in/madhuraghunayakula'
  },
  {
    name: 'Rajani Kasu',
    role: 'Co-founder & CTO',
    image: '/Rajani.png',
    description: 'Technology architect behind Doctorite\'s AI platform. Leads the engineering team building real-time session guidance, emotion detection, and automated clinical workflows.',
    color: 'from-violet-500 to-purple-600',
    url: 'https://www.linkedin.com/in/rajanikasu'
  },
];

export default function Team() {
  const { ref, inView } = useScrollAnimation();

  return (
    <section className="section-padding bg-gradient-to-b from-white to-gray-50" ref={ref}>
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
            The Team Behind the{' '}
            <span className="gradient-text">Intelligence</span>
          </h2>
          <p className="text-lg text-gray-600">
            Founded by healthcare professionals and technologists who believe
            AI should empower providers, not replace them.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {team.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="bg-white rounded-2xl p-8 shadow-lg shadow-brand-900/5 border border-gray-100 text-center group hover:shadow-xl transition-shadow duration-300"
            >
              <div className={`w-60 h-60 rounded-2xl bg-gradient-to-br ${member.color} mx-auto mb-5 shadow-lg group-hover:scale-105 transition-transform duration-300 overflow-hidden`}>
                <img src={member.image} alt={member.name} className={`w-full h-full object-cover ${member.imagePosition || ''}`} />
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-1">{member.name}</h3>
              <div className="text-sm font-semibold text-brand-600 mb-4">{member.role}</div>
              <p className="text-sm text-gray-500 leading-relaxed">{member.description}</p>

              <div className="mt-5">
                <a
                  href={member.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-brand-600 transition-colors"
                  aria-label={`${member.name} on LinkedIn`}
                >
                  <Linkedin className="w-4 h-4" />
                  LinkedIn
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
